import React, { useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { SupplierGroupSchema } from "constants/validation-schemas";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import { Input } from "components/Shared/mui-formik-inputs";
import { selectPermissions, selectPermissionsStatus, getAllPermissions } from "redux/slices/permissionsSlice";
import { translatePermission } from "util/helpers";
import { upperFirst } from "lodash";

const useStyles = makeStyles(() => ({

  cardContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 16px !important",
    margin: "0",
    '&:last-child': {
      paddingBottom: "8px !important",
    },
  },
  subheading: {
    fontWeight: 500,
    fontSize: "1.25rem",
    marginTop: 32,
    marginBottom: 16,
  },
}));

const RoleForm = ({ initialValues = {}, onSubmit, action }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const permissions = useSelector(selectPermissions);
  const loading = useSelector(selectPermissionsStatus);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAllPermissions());
  }, [dispatch]);
  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: SupplierGroupSchema,
    initialValues: {
      name: "",
      permissions: [],
      ...initialValues,
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        let processedPermissions;

        if (action === "EDIT") {
          processedPermissions = values.permissions;
        } else {
          const positivePermissions = values.permissions.filter(id => id > 0);
          const uncheckedDefaultPermIds = values.permissions
            .filter(id => id < 0)
            .map(id => -id);
          const defaultPermIds = permissions
            .filter(perm => perm.default && !uncheckedDefaultPermIds.includes(perm.id))
            .map(perm => perm.id);
          processedPermissions = [...new Set([...positivePermissions, ...defaultPermIds])];
        }

        const payload = {
          name: values.name,
          permissions: processedPermissions,
        };

        await onSubmit(payload);
      } catch (err) {
        console.error("Submit error:", err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    handleChange,
    setFieldValue,
    errors,
    handleSubmit,
    submitCount,
    isValid,
    isSubmitting,
    setFieldTouched,
  } = formik;
  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.module]) acc[perm.module] = [];
    acc[perm.module].push(perm);
    return acc;
  }, {});

  const handlePermissionToggle = (permissionId) => {
    const permission = permissions.find(p => p.id === permissionId);

    if (action === "EDIT") {
      if (values.permissions.includes(permissionId)) {
        setFieldValue("permissions", values.permissions.filter(id => id !== permissionId));
      } else {
        setFieldValue("permissions", [...values.permissions, permissionId]);
      }
      return;
    }

    const isCurrentlyChecked = values.permissions.includes(permissionId) ||
      (permission?.default && !values.permissions.includes(-permissionId));

    if (isCurrentlyChecked) {
      if (permission?.default) {
        setFieldValue("permissions", [...values.permissions.filter(id => id !== permissionId), -permissionId]);
      } else {
        setFieldValue("permissions", values.permissions.filter(id => id !== permissionId));
      }
    } else {
      if (permission?.default) {
        setFieldValue("permissions", values.permissions.filter(id => id !== -permissionId));
      } else {
        setFieldValue("permissions", [...values.permissions, permissionId]);
      }
    }
  };
  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);

  const closeHandler = () => {
    action === "ADD"
      ? history.push(PATHS.admin.roles)
      : history.push(PATHS.admin.rolesDetail.replace(":id", id));
  };

  return (
    <Box height="100%">
      <Box display="flex" mb={4}>
        <Box flex={2}>
          <Typography className={classes._heading} variant="h4">
            {action === "ADD" ? t("New Role") : t("Edit Role")}
          </Typography>
        </Box>
        <Box flex={2} textAlign="right" position="relative">
          <Box position="absolute" right={0}>
            <FormCancelSaveButton
              disabled={!isValid || isSubmitting}
              onCancel={closeHandler}
              onSave={handleSubmit}
              ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
            />
          </Box>
        </Box>
      </Box>

      <Typography className={classes.subheading}>{t("Basic Data")}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Input
            name="name"
            label={t("Name")}
            value={values.name}
            onChange={handleChange}
            errors={formik.errors}
            required
            disabled={action !== "ADD" && values?.name === 'Admin'}
          />
        </Grid>
      </Grid>

      <Typography className={classes.subheading}>{t("Permissions")}</Typography>
      {!permissions.length ? (
        <CircularProgress />
      ) : (
        Object.entries(groupedPermissions).map(([module, perms]) => (
          <Box key={module} mb={3}>
            <Typography variant="h6" gutterBottom>
              {t(module)}
            </Typography>
            <Grid container spacing={2}>
              {perms.map((perm) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={perm.id}>
                  <Card className={classes.card}>
                    <CardContent className={classes.cardContent}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              action === "EDIT"
                                ? values.permissions.includes(perm.id)
                                : (values.permissions.includes(perm.id) ||
                                  (perm?.default && !values.permissions.includes(-perm.id)))
                            }
                            onChange={() => handlePermissionToggle(perm.id)}
                            color="primary"
                          />
                        }
                        label={upperFirst(translatePermission(t, perm.module, perm.name))}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))

      )}
    </Box>
  );
};

export default RoleForm;
